import { getActiveCaregiverId, getActiveOfficeId } from '@lib/activeOffice';
import { clearMediaSession } from '@lib/mediaSession';
import keycloakIns from '@lib/keycloak';
import { getDefaulSocketConfig } from '@src/const/socketConfig';
import { useAuthStore } from '@stores/authStore';
import {
  NotificationType,
  SocketEvent,
  importantSystemNotificationForFrontend,
} from '@therame/shared';
import { defineStore } from 'pinia';
import { ReplaySubject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { ref, type Ref } from 'vue';

type NotificationSocketStore = {
  socket: Ref<Socket | undefined>;
  socketEndpoint: string;
  connect: () => Promise<void>;
  notificationTrigger: ReplaySubject<SocketEvent<'notification-received'>>;
  disconnectSocket: () => void;
};

export const useNotificationSocketStore = defineStore(
  'notificationSocket',
  (): NotificationSocketStore => {
    const authStore = useAuthStore();
    const socketEndpoint = (import.meta.env.VITE_WS_ROOT as string) + '/notifications';
    const socket = ref<Socket | undefined>();
    const notificationTrigger = new ReplaySubject<SocketEvent<'notification-received'>>();
    const reconnectInterval = ref<ReturnType<typeof setInterval>>();

    const disconnectSocket = () => {
      socket.value?.disconnect();
      clearInterval(reconnectInterval.value);
    };

    const connectSocket = async () => {
      return Promise.race([
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(false);
          }, 1000);
        }),
        new Promise<boolean>((resolve) => {
          socket.value = io(
            socketEndpoint,
            getDefaulSocketConfig(
              authStore.authContext?.token,
              getActiveOfficeId(),
              getActiveCaregiverId(),
            ),
          );
          socket.value.on('connect', () => {
            resolve(true);
          });
          socket.value?.on(
            'notification-received',
            (event: SocketEvent<'notification-received'>) => {
              const notification = event.props.notification;
              if (notification) {
                const isImportantSystemNotificationForFrontend =
                  importantSystemNotificationForFrontend.includes(notification.type);
                if (isImportantSystemNotificationForFrontend) {
                  if (notification.type === NotificationType.SYSTEM_USER_SESSIONS_ENDED) {
                    // Session serverseitig beendet → erzwungener Logout. Das
                    // Media-Cookie überlebt den Keycloak-Logout sonst bis zu 8 h.
                    void clearMediaSession().finally(() => keycloakIns.logout());
                  } else if (
                    notification.type === NotificationType.SYSTEM_USER_STRIPE_ENTITLEMENTS_UPDATED
                  ) {
                    authStore.refreshAuthContext();
                  }
                } else {
                  notificationTrigger.next(event);
                }
              }
            },
          );
          socket.value.connect();
        }),
      ]);
    };

    const connect = async () => {
      const connected = await connectSocket();
      if (!connected) {
        clearInterval(reconnectInterval.value);
        reconnectInterval.value = setInterval(async () => {
          const connected = await connectSocket();
          if (connected) {
            clearInterval(reconnectInterval.value);
          }
        }, 1000);
      }
    };

    const store: NotificationSocketStore = {
      socket,
      socketEndpoint,
      connect,
      notificationTrigger,
      disconnectSocket,
    };
    return store;
  },
);
